// src/components/ChatWidget.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../services/api';

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

interface ChatResponse {
  response: string;
}

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: 'Ahoj! Těším se na Vaše dotazy ohledně pobytu u nás na Chalupě Laura! 🏡😊',
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Prevent body scroll when chat is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 640) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleChat = useCallback(() => setIsOpen(o => !o), []);
  const appendMessage = (msg: ChatMessage) =>
    setChatHistory(prev => [...prev, msg].slice(-20)); // Keep last 20 messages

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [chatHistory, isOpen, isLoading]);

  const sendMessage = async () => {
    if (!userInput.trim()) return;
    const newUserMsg: ChatMessage = { role: 'user', content: userInput.trim() };
    setChatHistory(prev => [...prev, newUserMsg]);
    setUserInput('');
    setIsLoading(true);

    // Reset height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const response = await apiClient.post<ChatResponse>(
        '/chat',
        { messages: [...chatHistory.slice(-9), newUserMsg] }
      );
      const data = response.data;
      const botMsg: string = data?.response || 'Odpověď nebyla nalezena.';
      appendMessage({ role: 'assistant', content: botMsg });
    } catch {
      appendMessage({ role: 'assistant', content: 'Omlouvám se, ale momentálně se nemohu spojit se serverem.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  // autosize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'; // Max height limit
    }
  }, [userInput]);

  return (
    <>
      {/* Floating Button */}
      <div className={`fixed bottom-6 right-6 z-50 flex flex-col items-end gap-4 ${isOpen ? 'hidden sm:flex' : 'flex'}`}>
        
        {/* CTA Bubble (only when closed) */}
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="bg-white text-[#3f2b25] px-4 py-3 rounded-2xl rounded-br-none shadow-lg border border-[#e6dbd2] max-w-[200px] text-sm font-medium relative"
          >
            Potřebujete poradit? 👋
            {/* Close button for CTA */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                const parent = e.currentTarget.parentElement;
                if (parent) parent.style.display = 'none';
              }}
              className="absolute -top-2 -right-2 bg-gray-200 hover:bg-gray-300 rounded-full p-1 w-5 h-5 flex items-center justify-center text-xs text-gray-600"
            >
              ✕
            </button>
          </motion.div>
        )}

        <motion.button
          onClick={toggleChat}
          aria-label={isOpen ? "Zavřít chat" : "Otevřít chat"}
          className="flex items-center gap-3 bg-[#4B2E2E] text-white px-6 py-4 rounded-full shadow-2xl hover:bg-[#3a1e1e] transition-all focus:outline-none focus:ring-4 focus:ring-[#4B2E2E]/30"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isOpen ? (
             <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
             </svg>
          ) : (
            <>
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 006 21.75a6.721 6.721 0 003.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 01-.814 1.686.75.75 0 00.44 1.223zM8.25 10.875a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25zM10.875 12a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875-1.125a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25z" clipRule="evenodd" />
              </svg>
              <span className="font-bold text-lg tracking-wide">Chat</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed z-50 
              sm:top-auto sm:left-auto sm:bottom-20 sm:right-4 sm:w-[380px] sm:h-[600px] sm:max-h-[80vh] sm:rounded-2xl sm:shadow-2xl
              inset-0 w-full h-full bg-white flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#4B2E2E] text-white px-4 py-4 flex items-center justify-between shadow-md shrink-0">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.97-4.03 9-9 9a9.003 9.003 0 01-5.644-1.9L3 21l1.9-3.356A8.996 8.996 0 013 12c0-4.97 4.03-9 9-9s9 4.03 9 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg">Chalupa Laura</h3>
                  <p className="text-xs text-white/80 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    Online asistent
                  </p>
                </div>
              </div>
              <button 
                onClick={toggleChat}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Zavřít"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Messages Area */}
            <div 
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#f7f2ed]"
            >
              {chatHistory.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`
                      max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm
                      ${msg.role === 'user' 
                        ? 'bg-[#4B2E2E] text-white rounded-br-none' 
                        : 'bg-white text-gray-800 rounded-bl-none border border-gray-100'}
                    `}
                  >
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              
              {isLoading && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-none shadow-sm border border-gray-100 flex items-center gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100 shrink-0">
              <div className="flex items-end gap-2 bg-gray-50 p-2 rounded-3xl border border-gray-200 focus-within:border-[#4B2E2E] focus-within:ring-1 focus-within:ring-[#4B2E2E] transition-all">
                <textarea
                  ref={textareaRef}
                  value={userInput}
                  onChange={e => setUserInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Napište zprávu..."
                  rows={1}
                  className="flex-1 bg-transparent border-none focus:ring-0 resize-none max-h-32 py-3 px-2 text-sm"
                />
                <button
                  onClick={() => void sendMessage()}
                  disabled={isLoading || !userInput.trim()}
                  className="p-3 bg-[#4B2E2E] text-white rounded-full hover:bg-[#3a1e1e] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm mb-0.5"
                  aria-label="Odeslat"
                >
                  <svg className="h-5 w-5 transform rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
              <div className="text-center mt-2">
                <p className="text-[10px] text-gray-400">
                  Odpovídá AI asistent. Informace si prosím ověřte.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
